import type { StateMachineInput } from "@rive-app/react-canvas";

import type { PhonemeSpan } from "../api/transcription";
import { getVisemeMapping } from "../api/transcription";

export class VisemeAnimator {
  private inputValue = 0;
  private inputIncrementRate = 0;
  private currentVisemeInput?: StateMachineInput;
  private phonemeSpans: PhonemeSpan[];
  private getInputFromNumber: (
    inputNumber: string,
  ) => StateMachineInput | undefined;
  private visemeMapping: Record<string, string | undefined>;
  private startBrowserTimestamp?: number;
  private animationDuration: number;
  private audioStartTime = 0;

  private timeouts: NodeJS.Timeout[] = [];
  private isAnimating = false;
  private lastFrameTimestamp = 0;

  private constructor(
    phonemeSpans: PhonemeSpan[],
    getInputFromNumber: (inputNumber: string) => StateMachineInput | undefined,
    visemeMapping: Record<string, string | undefined>,
  ) {
    this.phonemeSpans = phonemeSpans;
    this.getInputFromNumber = getInputFromNumber;
    this.animationDuration =
      phonemeSpans.length > 0
        ? phonemeSpans[phonemeSpans.length - 1].end_time * 1000
        : 0;
    this.visemeMapping = visemeMapping;
  }

  /** Factory function to use in place of constructor */
  // We can't have an asynchronous function call in a constructor, so to
  // make sure that our viseme mapping is downloaded before the constructor
  public static makeAnimator = async (
    phonemeSpans: PhonemeSpan[],
    getInputFromNumber: (inputNumber: string) => StateMachineInput | undefined,
  ) => {
    const visemeMapping = await getVisemeMapping();
    return new VisemeAnimator(phonemeSpans, getInputFromNumber, visemeMapping);
  };

  public animate = (startTime = 0) => {
    this.startBrowserTimestamp = undefined;
    this.audioStartTime = startTime;
    this.isAnimating = true;
    this.timeouts = [];

    requestAnimationFrame(this.frame);

    this.phonemeSpans.forEach(phoneme => {
      if (phoneme.end_time >= startTime) {
        this.timeouts.push(
          setTimeout(
            () => {
              const inputNumber = this.visemeMapping[phoneme.phoneme];
              if (inputNumber) {
                this.setNextViseme(
                  inputNumber,
                  phoneme.end_time - phoneme.start_time,
                );
              }
            },
            (phoneme.start_time - startTime) * 1000,
          ),
        );
      }
    });
  };

  public stop = () => {
    this.timeouts.forEach(timeout => {
      clearTimeout(timeout);
    });

    this.isAnimating = false;
  };

  // when frame() is called, it should only need to know:
  //    - currentVisemeInput: the current viseme being animated
  //    - inputValue: the input value to put in this frame
  //    - inputIncrementRate: how much to increment the inputValueRef by for the next frame
  private frame = (ts: DOMHighResTimeStamp) => {
    if (this.startBrowserTimestamp === undefined) {
      this.startBrowserTimestamp = ts;
    }

    const deltaTime = (ts - this.lastFrameTimestamp) / 1000;
    const durationPlaying = ts - this.startBrowserTimestamp;

    if (this.currentVisemeInput) {
      this.currentVisemeInput.value = this.inputValue;
    }
    if (this.inputValue < 100) {
      this.inputValue = this.inputValue + deltaTime * this.inputIncrementRate;
    }
    if (
      durationPlaying < this.animationDuration - this.audioStartTime &&
      this.isAnimating
    ) {
      requestAnimationFrame(this.frame);
    }

    this.lastFrameTimestamp = ts;
  };

  private setNextViseme = (inputNumber: string, totalDuration: number) => {
    if (this.currentVisemeInput) {
      this.currentVisemeInput.value = 0;
    }
    this.currentVisemeInput = this.getInputFromNumber(inputNumber);
    this.inputValue = 0;
    this.inputIncrementRate = 200 / totalDuration;
  };
}
